import React from "react";
import ReportTable from "../ReportTable";
import { principleTitles, renderYesNo, safeText } from "./helpers";
import usePrincipleData from "./usePrincipleData";
import MultiHeaderReportTable from "../MultiHeaderReportTable";

const valueTableRows = (obj, keys, idPrefix) =>
  keys.map(([label, key], idx) => ({
    _id: `${idPrefix}-${idx}`,
    parameter: label,
    current: safeText(
      obj?.[key]?.currentFinancialYear ?? obj?.energyIntensity?.[key]?.currentFinancialYear,
      "N/A",
    ),
    previous: safeText(
      obj?.[key]?.previousFinancialYear ?? obj?.energyIntensity?.[key]?.previousFinancialYear,
      "N/A",
    ),
  }));

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const Principle6 = ({ styles }) => {
  const { loading, principle } = usePrincipleData(6);
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const energyConsumption = essential?.energyConsumptionIntensity || essential;

  const energyKeys = [
    ["Total electricity consumption (kWh)", "electricityConsumption"],
    ["Total fuel consumption (litres)", "fuelConsumption"],
    ["Energy consumption through other sources (kWh)", "energyConsumption"],
  ];

  const energyRenewableRowsBase = valueTableRows(
    energyConsumption?.fromRenewableSource,
    energyKeys,
    "p6-e1-ren",
  );
  const energyNonRenewableRowsBase = valueTableRows(
    energyConsumption?.fromNonRenewableSource,
    energyKeys,
    "p6-e1-non",
  );

  const energyRenewableRows = [
    ...energyRenewableRowsBase,
    {
      _id: "p6-e1-ren-total",
      parameter: "Total energy consumption from renewable sources (kWh)",
      current: (() => {
        const vals = energyRenewableRowsBase.map((r) => toNum(r.current)).filter((v) => v !== null);
        return vals.length ? String(vals.reduce((a, b) => a + b, 0)) : "N/A";
      })(),
      previous: (() => {
        const vals = energyRenewableRowsBase.map((r) => toNum(r.previous)).filter((v) => v !== null);
        return vals.length ? String(vals.reduce((a, b) => a + b, 0)) : "N/A";
      })(),
    },
  ];

  const energyNonRenewableRows = [
    ...energyNonRenewableRowsBase,
    {
      _id: "p6-e1-non-total",
      parameter: "Total energy consumption from non-renewable sources (kWh)",
      current: (() => {
        const vals = energyNonRenewableRowsBase.map((r) => toNum(r.current)).filter((v) => v !== null);
        return vals.length ? String(vals.reduce((a, b) => a + b, 0)) : "N/A";
      })(),
      previous: (() => {
        const vals = energyNonRenewableRowsBase.map((r) => toNum(r.previous)).filter((v) => v !== null);
        return vals.length ? String(vals.reduce((a, b) => a + b, 0)) : "N/A";
      })(),
    },
  ];

  const energyIntensityRows = valueTableRows(
    energyConsumption?.energyIntensity,
    [
      ["Total energy consumption from renewable & non-renewable sources (kWh)", "energyIntensity"],
      ["Energy intensity per rupee of turnover (kWh)", "energyIntensityTurnover"],
      [
        "Energy intensity per rupee of turnover adjusted for Purchasing Power Parity (kWh)",
        "energyIntensityPuchasingPower",
      ],
      ["Energy intensity in terms of physical output (kWh)", "energyPhysicalOutput"],
      ["Energy intensity (optional) (kWh)", "energyIntensityOptional"],
    ],
    "p6-e1-int",
  );

  const waterRows = valueTableRows(
    essential?.waterDisclosuresRelated,
    [
      ["Surface water", "surfaceWater"],
      ["Ground water", "groundWater"],
      ["Third party water", "thirdPartyWater"],
      ["Sea water / desalinated water", "seaWater"],
      ["Others", "others"],
      ["Total volume of water withdrawal", "totalVolumnWithdrawal"],
      ["Total volume of water consumption", "totalWaterConsumption"],
      ["Water intensity per rupee of turnover", "waterIntensityTurnover"],
      ["Water intensity per rupee of PPP", "waterIntensityPuchasingPower"],
      ["Water intensity in terms of physical output", "waterIntensityPhyiscalOutput"],
      ["Water intensity (optional)", "waterIntensityOptional"],
    ],
    "p6-e3",
  );

  const readTreatmentLevel = (obj) =>
    safeText(obj?.treatmentLevel ?? obj?.treatMentLeval, "-");

  const waterDischargeRows = [
    ["To surface water", "surfaceWater"],
    ["To Ground water", "groundWater"],
    ["To sea water", "producedWater"],
    ["Sent to third parties", "thirdPartyWater"],
    ["Others", "other"],
  ].flatMap(([label, key], idx) => {
    const c = essential?.waterDischargeCurrent?.[key] || {};
    const p = essential?.waterDischargePrevious?.[key] || {};
    return [
      {
        _id: `p6-e4-${idx}-0`,
        parameter: label,
        treatmentType: "Not treated",
        currentDischarge: safeText(c?.dischargeWithoutTreatment, "N/A"),
        currentTreatment: "-",
        previousDischarge: safeText(p?.dischargeWithoutTreatment, "N/A"),
        previousTreatment: "-",
      },
      {
        _id: `p6-e4-${idx}-1`,
        parameter: "",
        treatmentType: "With treatment",
        currentDischarge: safeText(c?.dischargeAfterTreatment, "N/A"),
        currentTreatment: readTreatmentLevel(c),
        previousDischarge: safeText(p?.dischargeAfterTreatment, "N/A"),
        previousTreatment: readTreatmentLevel(p),
      },
    ];
  });

  const waterDischargeTotalRow = {
    _id: "p6-e4-total",
    parameter: "Total water discharged (in kilolitres)",
    treatmentType: "",
    currentDischarge: safeText(essential?.waterDischargeCurrent?.total, "N/A"),
    currentTreatment: "",
    previousDischarge: safeText(essential?.waterDischargePrevious?.total, "N/A"),
    previousTreatment: "",
  };

  const ghgRows = valueTableRows(
    essential?.greenHouseGasEmissions,
    [
      ["Total Scope 1 emissions Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)", "scope1"],
      ["Total Scope 2 emissions Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)", "scope2"],
      ["Total Scope 1 and Scope 2 emission intensity per rupee of turnover", "totalOfScope1and2Intensity"],
      ["Total Scope 1 and Scope 2 emissions per rupee of turnover adjusted for Purchasing Power Parity (PPP)", "scope1and2emissionPPP"],
      ["Total Scope 1 and Scope 2 emission intensity in terms of physical output", "scope1and2emissionIntensityPhysical"],
      ["Total Scope 1 and Scope 2 emission intensity (optional)", "scope1and2EmissionIntensity"],
    ],
    "p6-e7",
  );

  const airRows = valueTableRows(
    essential?.airEmissons,
    [
      ["NOx", "nox"],
      ["Volatile organic compounds (VOC)", "voc"],
      ["SOx", "sox"],
      ["Particulate Matter (PM)", "pm"],
      ["Persistent organic pollutants (POP)", "pop"],
      ["Hazardous air pollutants (HAP)", "hap"],
    ],
    "p6-e6",
  );

  const otherAirRows = (Array.isArray(essential?.airEmissionsOthers)
    ? essential.airEmissionsOthers
    : []
  ).map((row, idx) => ({
    _id: row?._id || `p6-e6-other-${idx}`,
    parameter: safeText(row?.parameter, "N/A"),
    current: safeText(row?.currentFinancialYear, "N/A"),
    previous: safeText(row?.previousFinancialYear, "N/A"),
  }));

  const wasteGeneratedRows = valueTableRows(
    essential?.wasteGenerated,
    [
      ["Plastic waste", "plastics"],
      ["E-waste", "eWaste"],
      ["Bio-medical waste", "bioMediacal"],
      ["Construction and demolition waste", "construtionAndDemolition"],
      ["Battery waste", "batteryWaste"],
      ["Radioactive waste", "radioactiveWaste"],
      ["Other hazardous waste", "otherHazardous"],
      ["Other non-hazardous waste", "otherNonHazardous"],
    ],
    "p6-e9-gen",
  );

  const wasteRecoveredRows = valueTableRows(
    essential?.wasteGeneratedCategory,
    [
      ["Plastic waste", "plastics"],
      ["E-waste", "eWaste"],
      ["Bio-medical waste", "bioMediacal"],
      ["Construction and demolition waste", "construtionAndDemolition"],
      ["Battery waste", "batteryWaste"],
      ["Radioactive waste", "radioactiveWaste"],
      ["Other hazardous waste", "otherHazardous"],
      ["Other non-hazardous waste", "otherNonHazardous"],
    ],
    "p6-e9-rec",
  );

  const wasteDisposedRows = valueTableRows(
    essential?.wasteDesposed,
    [
      ["Plastic waste", "plastics"],
      ["E-waste", "eWaste"],
      ["Bio-medical waste", "bioMediacal"],
      ["Construction and demolition waste", "construtionAndDemolition"],
      ["Battery waste", "batteryWaste"],
      ["Radioactive waste", "radioactiveWaste"],
      ["Other hazardous waste", "otherHazardous"],
      ["Other non-hazardous waste", "otherNonHazardous"],
    ],
    "p6-e9-dis",
  );

  const projectRows = (Array.isArray(essential?.projectUndertakenByentity)
    ? essential.projectUndertakenByentity
    : []
  ).map((row, idx) => ({
    _id: row?._id || `p6-e12-${idx}`,
    project: safeText(row?.nameOfProject, "N/A"),
    brief: safeText(row?.projectBrief, "N/A"),
    notification: safeText(row?.EIANotification, "N/A"),
    date: safeText(row?.dateOfNotification, "N/A"),
    publicDomain: safeText(row?.publicDomain, "N/A"),
    agency: safeText(row?.extenalAgecy, "N/A"),
    link: safeText(row?.link, "N/A"),
  }));

  const environmentalApprovalRows = (Array.isArray(essential?.environmentalApproval)
    ? essential.environmentalApproval
    : []
  ).map((row, idx) => ({
    _id: row?._id || `p6-e10-${idx}`,
    location: safeText(row?.location, "N/A"),
    operation: safeText(row?.operation, "N/A"),
    approval: safeText(row?.approval, "N/A"),
    reason: safeText(row?.reason, "N/A"),
  }));

  const nonComplianceRows = (Array.isArray(essential?.entityComplainwithEnvironmentLaw)
    ? essential.entityComplainwithEnvironmentLaw
    : []
  ).map((row, idx) => ({
    _id: row?._id || `p6-e12-${idx}`,
    law: safeText(row?.specifyLaw, "N/A"),
    details: safeText(row?.details, "N/A"),
    penalties: safeText(row?.penalties, "N/A"),
    correctiveAction: safeText(row?.correctiveAction, "N/A"),
  }));

  const l1Facilities = Array.isArray(leadership?.waterWithDrawalConsumptionAndDischarg)
    ? leadership.waterWithDrawalConsumptionAndDischarg
    : [];

  const l1WithdrawalRows = (facility, idx) =>
    [
      ["(i) Surface water", "surfaceWater"],
      ["(ii) Ground water", "groundWater"],
      ["(iii) Third party water", "thirdPartyWater"],
      ["(iv) Seawater / desalinated water", "seaWater"],
      ["(v) Others", "other"],
      ["Total volume of water withdrawal", "total"],
      ["Total volume of water consumption (in kiloliters)", "totalWaterConsumption"],
      ["Water intensity per rupee of turnover (Water consumed / turnover)", "waterIntensityPerRupee"],
      ["Water intensity (optional) - the relevant metric may be selected by the entity", "waterIntensity"],
    ].map(([parameter, key], rowIdx) => ({
      _id: `p6-l1-w-${idx}-${rowIdx}`,
      parameter,
      current: safeText(facility?.waterWithdrawal?.[key]?.currentFinancialYear, "N/A"),
      previous: safeText(facility?.waterWithdrawal?.[key]?.previousFinancialYear, "N/A"),
    }));

  const l1DischargeRows = (facility, idx) => {
    const mapKeys = [
      ["To surface water", "toSurfaceWater"],
      ["To Ground water", "toGroundwater"],
      ["To sea water", "toProducedWater"],
      ["Sent to third parties", "toThirdParties"],
      ["Others", "others"],
    ];
    const rows = mapKeys.flatMap(([label, key], rowIdx) => {
      const c = facility?.waterDischargeCurrent?.[key] || {};
      const p = facility?.waterDischargePrevious?.[key] || {};
      return [
        {
          _id: `p6-l1-d-${idx}-${rowIdx}-0`,
          parameter: label,
          treatmentType: "Not treated",
          currentDischarge: safeText(c?.dischargeWithoutTreatment, "N/A"),
          currentTreatment: "-",
          previousDischarge: safeText(p?.dischargeWithoutTreatment, "N/A"),
          previousTreatment: "-",
        },
        {
          _id: `p6-l1-d-${idx}-${rowIdx}-1`,
          parameter: "",
          treatmentType: "With treatment",
          currentDischarge: safeText(c?.dischargeAfterTreatment, "N/A"),
          currentTreatment: safeText(c?.treatmentLevel, "-"),
          previousDischarge: safeText(p?.dischargeAfterTreatment, "N/A"),
          previousTreatment: safeText(p?.treatmentLevel, "-"),
        },
      ];
    });

    rows.push({
      _id: `p6-l1-d-${idx}-total`,
      parameter: "Total water discharged",
      treatmentType: "",
      currentDischarge: safeText(facility?.waterDischargeCurrent?.total, "N/A"),
      currentTreatment: "",
      previousDischarge: safeText(facility?.waterDischargePrevious?.total, "N/A"),
      previousTreatment: "",
    });
    return rows;
  };

  const l2Rows = valueTableRows(
    leadership?.scope3EmissionIntensity,
    [
      [
        "Total Scope 3 emissions (Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)",
        "totalScope3Emission",
      ],
      ["Total Scope 3 emissions per rupee turnover", "totalScope3EmissionPerRupeeturnOver"],
      [
        "Total Scope 3 emission intensity (optional) - the relevant metric may be selected by the entity",
        "emissionIntensity",
      ],
    ],
    "p6-l2",
  );

  const l4Rows = (Array.isArray(leadership?.spcificIntiativesorUsedInnovative)
    ? leadership.spcificIntiativesorUsedInnovative
    : []
  ).map((row, idx) => ({
    _id: row?._id || `p6-l4-${idx}`,
    initiative: safeText(row?.intiativeundertaken, "N/A"),
    detail: safeText(row?.detail, "N/A"),
    webLink: safeText(row?.webLink, "N/A"),
    outcome: safeText(row?.outCome, "N/A"),
  }));

  return (
    <div className={styles.principleBlock}>
      <div className={styles.principleHeading}>{`PRINCIPLE 6: ${principleTitles[5]}`}</div>
      {loading ? <div className={styles.meta}>Loading principle 6...</div> : null}

      <div className={styles.band}>I. ESSENTIAL INDICATOR</div>

      <div className={styles.question}>
        1. Details of total energy consumption (in Joules or multiples) and energy intensity.
      </div>
      <div className={styles.answer}>a. From Renewable Sources</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Parameter" },
          { field: "current", label: "FY 2025-26" },
          { field: "previous", label: "FY 2024-25" },
        ]}
        rows={energyRenewableRows}
      />
      <div className={styles.answer}>b. From Non-Renewable Sources</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Parameter" },
          { field: "current", label: "FY 2025-26" },
          { field: "previous", label: "FY 2024-25" },
        ]}
        rows={energyNonRenewableRows}
      />
      <div className={styles.answer}>c. Energy Intensity</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Parameter" },
          { field: "current", label: "FY 2025-26" },
          { field: "previous", label: "FY 2024-25" },
        ]}
        rows={energyIntensityRows}
      />

      <div className={styles.question}>
        2. Does the entity have any sites / facilities identified as designated consumers (DCs) under the Performance, Achieve and Trade (PAT) Scheme of the Government of India? If yes, disclose whether targets set under the PAT scheme have been achieved. In case targets have not been achieved, provide the remedial action taken, if any.
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.patSchemeDetails?.action)}
        {essential?.patSchemeDetails?.action
          ? `, ${safeText(essential?.patSchemeDetails?.remark, "N/A")}`
          : ""}
      </div>

      <div className={styles.question}>
        3. Provide details of the following disclosures related to water, in the following format:
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Category" },
          { field: "current", label: "Current Financial Year" },
          { field: "previous", label: "Previous Financial Year" },
        ]}
        rows={waterRows}
      />
      <div className={styles.question}>
        If any independent assessment/ evaluation/assurance has been carried out by an external agency? (Yes / No)
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.waterDisclosuresRelated?.externalAgency?.action)}
        {String(essential?.waterDisclosuresRelated?.externalAgency?.action) === "true"
          ? `, ${safeText(
              Array.isArray(essential?.waterDisclosuresRelated?.externalAgency?.name)
                ? essential.waterDisclosuresRelated.externalAgency.name.filter(Boolean).join(", ")
                : essential?.waterDisclosuresRelated?.externalAgency?.name,
              "N/A",
            )}`
          : ""}
      </div>

      <div className={styles.question}>
        4. Provide the following details related to water discharged: Water discharge by destination and level of treatment (in kilolitres)
      </div>
      <MultiHeaderReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Category" },
          { field: "treatmentType", label: "" },
          {
            label: "Current Financial Year",
            children: [
              { field: "currentDischarge", label: "Water Discharge (in KL)" },
              { field: "currentTreatment", label: "Specify level of treatment" },
            ],
          },
          {
            label: "Previous Financial Year",
            children: [
              { field: "previousDischarge", label: "Water Discharged (in KL)" },
              { field: "previousTreatment", label: "Specify level of treatment" },
            ],
          },
        ]}
        rows={[...waterDischargeRows, waterDischargeTotalRow]}
      />
      <div className={styles.question}>
        If any independent assessment/ evaluation/assurance has been carried out by an external agency? (Yes / No)
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.waterDischargeExternalAgency?.action)}
        {String(essential?.waterDischargeExternalAgency?.action) === "true"
          ? `, ${safeText(
              Array.isArray(essential?.waterDischargeExternalAgency?.name)
                ? essential.waterDischargeExternalAgency.name.filter(Boolean).join(", ")
                : essential?.waterDischargeExternalAgency?.name,
              "N/A",
            )}`
          : ""}
      </div>

      <div className={styles.question}>
        5. Has the entity implemented a mechanism for Zero Liquid Discharge? If yes, provide details of its coverage and implementation.
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.zeroLiquidDischarge?.action)}
        {essential?.zeroLiquidDischarge?.action
          ? `, ${safeText(essential?.zeroLiquidDischarge?.remark, "N/A")}`
          : ""}
      </div>

      <div className={styles.question}>
        6. Please provide details of air emissions (other than GHG emissions) by the entity, in the following format:
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Category" },
          { field: "current", label: "Current Financial Year" },
          { field: "previous", label: "Previous Financial Year" },
        ]}
        rows={airRows}
      />
      {otherAirRows.length ? (
        <>
          <div className={styles.answer}>Other air emission parameters (if any)</div>
          <ReportTable
            showSerialNumber={false}
            columns={[
              { field: "parameter", label: "Category" },
              { field: "current", label: "Current Financial Year" },
              { field: "previous", label: "Previous Financial Year" },
            ]}
            rows={otherAirRows}
          />
        </>
      ) : null}
      <div className={styles.question}>
        If any independent assessment/ evaluation/assurance has been carried out by an external agency? (Yes / No)
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.airEmissionAssessment?.action)}
        {String(essential?.airEmissionAssessment?.action) === "true"
          ? `, ${safeText(
              Array.isArray(essential?.airEmissionAssessment?.name)
                ? essential.airEmissionAssessment.name.filter(Boolean).join(", ")
                : essential?.airEmissionAssessment?.name,
              "N/A",
            )}`
          : ""}
      </div>

      <div className={styles.question}>
        7. Provide details of greenhouse gas emissions (Scope 1 and Scope 2 emissions) & its intensity, in the following format:
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Category" },
          { field: "current", label: "Current Financial Year" },
          { field: "previous", label: "Previous Financial Year" },
        ]}
        rows={ghgRows}
      />

      <div className={styles.question}>
        8. Does the entity have any project related to reducing Green House Gas emission? If Yes, then provide details.
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.greenHouseGasBooleanEmissions?.action)}
        {essential?.greenHouseGasBooleanEmissions?.action
          ? `, ${safeText(essential?.greenHouseGasBooleanEmissions?.remark, "N/A")}`
          : ""}
      </div>
      {/* <div className={styles.question}>
        If any independent assessment/ evaluation/assurance has been carried out by an external agency? (Yes / No)
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.independentAssessment?.action)}
        {String(essential?.independentAssessment?.action) === "true"
          ? `, ${safeText(
              Array.isArray(essential?.independentAssessment?.name)
                ? essential.independentAssessment.name.filter(Boolean).join(", ")
                : essential?.independentAssessment?.name,
              "N/A",
            )}`
          : ""}
      </div> */}

      <div className={styles.question}>9. Provide details related to waste management by the entity</div>
      <div className={styles.answer}>a. Waste generated (in metric tonnes)</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Parameter" },
          { field: "current", label: "Current Financial Year" },
          { field: "previous", label: "Previous Financial Year" },
        ]}
        rows={wasteGeneratedRows}
      />
      <div className={styles.answer}>
        b. For each category of waste generated, total waste recovered through recycling, re-using or other recovery operations (in metric tonnes)
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Parameter" },
          { field: "current", label: "Current Financial Year" },
          { field: "previous", label: "Previous Financial Year" },
        ]}
        rows={wasteRecoveredRows}
      />
      <div className={styles.answer}>
        c. For each category of waste generated, total waste disposed by nature of disposal method (in metric tonnes)
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Parameter" },
          { field: "current", label: "Current Financial Year" },
          { field: "previous", label: "Previous Financial Year" },
        ]}
        rows={wasteDisposedRows}
      />
      <div className={styles.question}>
        Indicate if any independent assessment/ evaluation/assurance has been carried out by an external agency? If yes, name of the external agency.
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.independentAssessment?.action)}
        {String(essential?.independentAssessment?.action) === "true"
          ? `, ${safeText(
              Array.isArray(essential?.independentAssessment?.name)
                ? essential.independentAssessment.name.filter(Boolean).join(", ")
                : essential?.independentAssessment?.name,
              "N/A",
            )}`
          : ""}
      </div>

      <div className={styles.question}>
        10. Briefly describe the waste management practices adopted in your establishments. Describe the strategy adopted by your company to reduce usage of hazardous and toxic chemicals in your products and processes and the practices adopted to manage such wastes.
      </div>
      <div className={styles.answer}>
        Provide steps taken to address the same.
        <br />
        {safeText(essential?.wasterManagmentPractices, "N/A")}
      </div>

      <div className={styles.question}>
        11. If the entity has operations/offices in/around ecologically sensitive sensitive areas (such as national parks, wildlife sanctuaries, biosphere reserves, wetlands, biodiversity hotspots, forests, coastal regulation zones etc.) where environmental approvals / clearances are required, please specify details in the following format:
      </div>
      <ReportTable
        showSerialNumber={true}
        columns={[
          { field: "location", label: "Location of operations/offices" },
          { field: "operation", label: "Type of operations" },
          {
            field: "approval",
            label:
              "Whether the conditions of environmental approval / clearance are being complied with? (Y/N)",
          },
          { field: "reason", label: "If no, the reasons thereof and corrective action taken, if any" },
        ]}
        rows={environmentalApprovalRows}
      />

      <div className={styles.question}>
        12. Details of environmental impact assessments of projects undertaken by the entity based on applicable laws, in the current financial year:
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "project", label: "Name of Project" },
          { field: "brief", label: "Brief Details of Project" },
          { field: "notification", label: "EIA Notification No." },
          { field: "date", label: "Date of Notification" },
          { field: "agency", label: "Whether conducted by independent external agency" },
          { field: "publicDomain", label: "Results communicated in public domain" },
          { field: "link", label: "Link" },
        ]}
        rows={projectRows}
      />

      <div className={styles.question}>
        13. Is the entity compliant with the applicable environmental law/ regulations/ guidelines in India; such as the Water (Prevention and Control of Pollution) Act, Air (Prevention and Control of Pollution) Act, Environment protection act and rules thereunder. If not, provide details of all such non-compliances, in the following format:
      </div>
      <div className={styles.answer}>{renderYesNo(essential?.entityComplain)}</div>
      {String(essential?.entityComplain) === "true" ? (
        <ReportTable
          showSerialNumber={true}
          columns={[
            { field: "law", label: "Specify the law / regulation / guidelines (not complied)" },
            { field: "details", label: "Provide details of the non- compliance" },
            { field: "penalties", label: "Any fines / penalties / action taken by regulatory agencies such as pollution control boards or by courts" },
            { field: "correctiveAction", label: "Corrective action taken, if any" },
          ]}
          rows={nonComplianceRows}
        />
      ) : null}

      <div className={styles.band}>II. LEADERSHIP INDICATOR</div>

      <div className={styles.question}>
        1. Water withdrawal, consumption and discharge in areas of water stress (in kilolitres): For each facility / plant located in areas of water stress, provide the following information:
      </div>
      {l1Facilities.map((facility, idx) => (
        <div key={facility?._id || `p6-l1-f-${idx}`}>
          <div className={styles.answer}>{`Facility: ${safeText(facility?.facilityName, "N/A")}`}</div>
          <div className={styles.answer}>{`(i) Name of area: ${safeText(facility?.nameOfArea, "N/A")}`}</div>
          <div className={styles.answer}>{`(ii) Nature of Operation: ${safeText(facility?.operation, "N/A")}`}</div>
          <div className={styles.answer}>(iii) Water withdrawal, consumption and discharge by Source</div>
          <div className={styles.answer}>Water withdrawal by source (in kiloliters)</div>
          <ReportTable
            showSerialNumber={false}
            columns={[
              { field: "parameter", label: "Parameter" },
              { field: "current", label: "Current Financial Year" },
              { field: "previous", label: "Previous Financial Year" },
            ]}
            rows={l1WithdrawalRows(facility, idx)}
          />
          <MultiHeaderReportTable
            showSerialNumber={false}
            columns={[
              { field: "parameter", label: "Parameter" },
              { field: "treatmentType", label: "" },
              {
                label: "Current Financial Year",
                children: [
                  { field: "currentDischarge", label: "Water Discharge (in KL)" },
                  { field: "currentTreatment", label: "Specify level of treatment" },
                ],
              },
              {
                label: "Previous Financial Year",
                children: [
                  { field: "previousDischarge", label: "Water Discharged (in KL)" },
                  { field: "previousTreatment", label: "Specify level of treatment" },
                ],
              },
            ]}
            rows={l1DischargeRows(facility, idx)}
          />
        </div>
      ))}
      <div className={styles.question}>
        2. Please provide details of total Scope 3 emissions & its intensity, in the following format:
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "parameter", label: "Category" },
          { field: "current", label: "Current Financial Year" },
          { field: "previous", label: "Previous Financial Year" },
        ]}
        rows={l2Rows}
      />
      <div className={styles.question}>
        If any independent assessment/ evaluation/assurance has been carried out by an external agency? (Yes / No)
      </div>
      <div className={styles.answer}>
        {renderYesNo(leadership?.waterIndependantAssessment?.action)}
        {String(leadership?.waterIndependantAssessment?.action) === "true"
          ? `, ${safeText(
              Array.isArray(leadership?.waterIndependantAssessment?.name)
                ? leadership.waterIndependantAssessment.name.filter(Boolean).join(", ")
                : leadership?.waterIndependantAssessment?.name,
              "N/A",
            )}`
          : ""}
      </div>

      <div className={styles.question}>
        3. With respect to the ecologically sensitive areas reported at Question 11 of Essential Indicators above, provide details of significant direct & indirect impact of the entity on biodiversity in such areas along-with prevention and remediation activities.
      </div>
      <div className={styles.answer}>{safeText(leadership?.bioDiversityPrevention, "N/A")}</div>

      <div className={styles.question}>
        4. If the entity has undertaken any specific initiatives or used innovative technology or solutions to improve resource efficiency, or reduce impact due to emissions / effluent discharge / waste generated, please provide details of the same as well as outcome of such initiatives, as per the following format:
      </div>
      <MultiHeaderReportTable
        showSerialNumber={true}
        columns={[
          { field: "initiative", label: "Initiative undertaken" },
          {
            label: "Details of initiative",
            children: [
              { field: "detail", label: "Summary" },
              { field: "webLink", label: "Weblink" },
            ],
          },
          { field: "outcome", label: "Outcome of the initiative" },
        ]}
        rows={l4Rows}
      />

      <div className={styles.question}>
        5. Does the entity have a business continuity and disaster management plan? Give details in 100 words / web link.
      </div>
      <div className={styles.answer}>
        {renderYesNo(leadership?.busineessDisasterPlan?.action)}
      </div>
      {String(leadership?.busineessDisasterPlan?.action) === "true" ? (
        <div className={styles.answer}>
          {safeText(leadership?.busineessDisasterPlan?.remark, "N/A")}
          <br />
          {`Web Link (if any): ${safeText(leadership?.busineessDisasterPlan?.webLink, "N/A")}`}
        </div>
      ) : null}

      <div className={styles.question}>
        6. Disclose any significant adverse impact to the environment, arising from the value chain of the entity. What mitigation or adaptation measures have been taken by the entity in this regard.
      </div>
      <div className={styles.answer}>
        {safeText(leadership?.disCloseAdverseImpactEnvironment, "N/A")}
      </div>

      <div className={styles.question}>
        7. Percentage of value chain partners (by value of business done with such partners) that were assessed for environmental impacts.
      </div>
      <div className={styles.answer}>{safeText(leadership?.valueChainPartnerAssessment, "N/A")}</div>

      <div className={styles.question}>8. How many Green Credits have been generated or procured.</div>
      <div className={styles.answer}>
        {`a. By the listed entity
${safeText(leadership?.greenCreditsByEntityCount, "N/A")} (${safeText(
          leadership?.greenCreditsByEntityRemark,
          "N/A",
        )})

b. By the top ten (in terms of value of purchases and sales, respectively) value chain partners
${safeText(leadership?.greenCreditsByValueChainPartnerCount, "N/A")} (${safeText(
          leadership?.greenCreditsByValueChainPartnerRemark,
          "N/A",
        )})`}
      </div>

      <div className={styles.question}>
        9. Provide the following details related to water discharged: Water discharge by destination and level of treatment (in kilolitres)
      </div>
      <div className={styles.answer}>
        {renderYesNo(leadership?.zeroLiquidDischarge?.action)}
        {leadership?.zeroLiquidDischarge?.action
          ? `, ${safeText(leadership?.zeroLiquidDischarge?.remark, "N/A")}`
          : ""}
      </div>
      <div className={styles.pageBreak} />
    </div>
  );
};

export default Principle6;
